// Picks up new deploys automatically, on phones (installed app) and desktop.
//
// Checks for a new build when the app opens, when it comes back to the
// foreground, when the network returns, and every 10 minutes. When one is
// found:
//   - the app reloads by itself if it was in the background for 30+ minutes
//     (e.g. opened again the next morning) or as soon as the user moves to
//     another screen, so nothing typed on the current screen is lost;
//   - meanwhile a "New version available" notice offers "Update now".
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { checkForUpdate, reloadForUpdate } from "@/lib/appUpdate";

const CHECK_EVERY_MS = 10 * 60 * 1000;
const AWAY_RELOAD_MS = 30 * 60 * 1000;

export default function AppUpdateWatcher({ enabled = process.env.NODE_ENV === "production" }) {
  const location = useLocation();
  const pending = useRef(null); // bundle name of the waiting update
  const hiddenAt = useRef(null);
  const firstPath = useRef(true);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;

    const announce = (target) => {
      if (pending.current === target) return;
      pending.current = target;
      toast("New version available", {
        id: "app-update",
        description: "The app will update when you open another screen.",
        duration: Infinity,
        action: { label: "Update now", onClick: () => reloadForUpdate(target) },
      });
    };

    const check = async ({ reloadIfFound = false } = {}) => {
      const target = await checkForUpdate();
      if (stopped || !target) return;
      if (reloadIfFound && reloadForUpdate(target)) return;
      announce(target);
    };

    const updateServiceWorker = () => {
      navigator.serviceWorker?.getRegistration?.()
        .then((reg) => reg && reg.update())
        .catch(() => {});
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt.current = Date.now();
        return;
      }
      const away = hiddenAt.current ? Date.now() - hiddenAt.current : 0;
      hiddenAt.current = null;
      updateServiceWorker();
      check({ reloadIfFound: away >= AWAY_RELOAD_MS });
    };
    const onOnline = () => check();

    check();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") check();
    }, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [enabled]);

  // Moving to another screen is a safe moment to load the new version.
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    if (enabled && pending.current) reloadForUpdate(pending.current);
  }, [location.pathname, enabled]);

  return null;
}
